/**
 * Shared booking money + source helpers for the CRM bookings table and exports.
 *
 * Unit notes (the DB is mixed by design):
 *  - Website/WhatsApp bookings: `total_before_discount` in RUPEES, `payment_amount` in PAISE.
 *  - Legacy/test (DoozyBot seed) bookings: `total_amount` + `amount_paid` in RUPEES.
 */

// Total booking value (rupees), net of any applied discounts.
const grossTotal = (b) =>
  Number(b.total_before_discount || b.total_amount || b.package_price || 0);

const appliedDiscount = (b) =>
  Number(b.referral_discount || 0) +
  Number(b.coupon_discount || 0) +
  Number(b.early_bird_discount || 0);

/**
 * Derive Total / Paid / Balance (all rupees) and a payment status of
 * "paid" | "incomplete" | "pending".
 *  - pending     : nothing received yet
 *  - paid        : full amount settled (full payment, or test ₹1 flow)
 *  - incomplete  : advance received, balance still due at setup
 */
export const computePayment = (b) => {
  const paidFlag = (b.payment_status || "").toLowerCase() === "paid";
  const payType = (b.payment_type || "").toLowerCase();
  const total = Math.max(grossTotal(b) - appliedDiscount(b), 0);

  let paid;
  if (b.amount_paid != null && b.payment_amount == null) {
    paid = Number(b.amount_paid); // legacy/test bookings (already rupees)
  } else {
    const chargedRs = Math.round(Number(b.payment_amount || 0) / 100);
    paid = paidFlag ? chargedRs : 0;
  }

  let balance = 0;
  let status = "pending";
  if (paid <= 0) {
    status = "pending";
    balance = total;
  } else if (payType === "test") {
    // ₹1 team-test transaction — treat the booking as fully settled.
    status = "paid";
    balance = 0;
  } else {
    balance = Math.max(total - paid, 0);
    status = balance > 0 ? "incomplete" : "paid";
  }

  return { total, paid, balance, status };
};

export const PAYMENT_STATUS_META = {
  paid: { label: "Paid", cls: "bg-green-100 text-green-700" },
  incomplete: { label: "Incomplete Payment", cls: "bg-orange-100 text-orange-700" },
  pending: { label: "Pending", cls: "bg-yellow-100 text-yellow-700" },
};

const SOURCE_META = {
  whatsapp: { label: "WhatsApp", cls: "bg-[#25D366]/15 text-[#128C7E]" },
  website: { label: "Website", cls: "bg-blue-100 text-blue-700" },
  manual: { label: "Manual", cls: "bg-gray-100 text-gray-600" },
  lead: { label: "Lead", cls: "bg-purple-100 text-purple-700" },
  doozygo_magic: { label: "DoozyGo Magic", cls: "bg-[#8b5cf6]/15 text-[#8b5cf6]" },
};

// Prefer the explicit Magic origin, then the channel source.
export const bookingSourceMeta = (b) => {
  if ((b.booking_source || "").toUpperCase() === "DOOZYGO_MAGIC") return SOURCE_META.doozygo_magic;
  const raw = (b.source || b.booking_source || "").toLowerCase();
  if (raw.includes("whatsapp")) return SOURCE_META.whatsapp;
  return SOURCE_META[raw] || { label: b.source || "Website", cls: "bg-blue-100 text-blue-700" };
};

export const rupee = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export const getAddOns = (b) =>
  (b.add_ons || []).map((a) => (typeof a === "string" ? a : a.name || a.id || "")).filter(Boolean);
